import type { Rarity } from "@/domain/gems-game/domain/lib/Earning";
import { lighten } from "@mui/material";

/**
 * The canonical order of rarity levels from lowest to highest.
 * Used for progression, display order, and tier calculations.
 */
export const RARITY_LEVELS: Rarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Epic",
  "Legendary",
];

/**
 * Get the numeric tier level for a given rarity (1-based).
 * Returns 1 (Common) if rarity is undefined.
 */
export const getRarityTierLevel = (rarity: Rarity | undefined): number => {
  if (!rarity) return 1;
  const index = RARITY_LEVELS.indexOf(rarity);
  return index === -1 ? 1 : index + 1;
};

/**
 * Get the next rarity level after the given one.
 * Returns undefined if already at Legendary.
 */
export const getNextRarity = (rarity: Rarity): Rarity | undefined => {
  const currentIndex = RARITY_LEVELS.indexOf(rarity);
  return currentIndex < RARITY_LEVELS.length - 1
    ? RARITY_LEVELS[currentIndex + 1]
    : undefined;
};

/**
 * Get the previous rarity level before the given one.
 * Returns undefined if already at Common.
 */
export const getPreviousRarity = (rarity: Rarity): Rarity | undefined => {
  const currentIndex = RARITY_LEVELS.indexOf(rarity);
  return currentIndex > 0 ? RARITY_LEVELS[currentIndex - 1] : undefined;
};

/**
 * Foreground/icon colours for each rarity. Usually slightly brighter/lighter versions.
 */
export const RARITY_COLOR: Record<Rarity, string> = {
  Common: "#A0A0A0",
  Uncommon: "#3CD25B",
  Rare: "#46ACFF",
  Epic: "#9C2DFF",
  Legendary: "#FFC107",
};

/**
 * Linear gradient strings for each rarity, used for text/colorful backgrounds.
 */
export const RARITY_GRADIENT_MAP: Record<Rarity, string> = {
  Common: "linear-gradient(90deg, #6D6C6D, #A0A0A0)",
  Uncommon: "linear-gradient(90deg, #3CD25B, #7AFFAA)",
  Rare: "linear-gradient(90deg, rgb(70, 172, 255), rgb(138, 220, 255))",
  Epic: "linear-gradient(90deg, rgb(156, 45, 255), rgb(210, 145, 255))",
  Legendary: "linear-gradient(90deg, rgb(255, 181, 22), rgb(255, 215, 115))",
};

export const getLighterRarityColor = (rarity: Rarity, amount = 0.1): string =>
  lighten(RARITY_COLOR[rarity], amount);

export const calculateMythicPercentage = (
  total_imx?: number,
  total_imx_sans_jackpots?: number,
): number => {
  if (!total_imx) {
    return 0;
  }

  const mythicPercentage =
    ((total_imx - (total_imx_sans_jackpots || 0)) / total_imx) * 100;
  return Math.max(1, Math.ceil(mythicPercentage));
};
